/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { redirect, useRouteLoaderData } from 'react-router';
import type { Route } from './+types/_app._index';
import { fetchWishlistInitialState } from '@/lib/wishlist/fetch-initial-state.server';
import type { WishlistInitialState } from '@/lib/wishlist/state';
import { WishlistProvider } from '@/providers/wishlist';
import { resolvePrefix } from '@salesforce/storefront-next-runtime/site-context';
import { Region } from '@/components/region';
import { getConfig } from '@salesforce/storefront-next-runtime/config';
import { PageType } from '@/lib/decorators/page-type';
import { RegionDefinition } from '@/lib/decorators/region-definition';
import { resolveLastComponentTheme } from '@/lib/page-designer/last-component-theme';
import { type DefaultSettingsTheme } from '@/lib/page-designer/default-settings';

import { fetchPageWithComponentData } from '@/lib/page-designer/page-loader.server';
import { getLogger } from '@/lib/logger.server';
import type { Logger } from '@/lib/logger';

import HeroFallback from '@/components/hero/hero-fallback';
import { SeoMeta } from '@/components/seo-meta';
import { JsonLd } from '@/components/json-ld';
import { generateHomepageSchema, type HomepageSchema } from '@/utils/organization-schema';
import { getPublicOrigin } from '@/utils/schema-url';
import { buildCanonicalUrl } from '@/utils/canonical-url';
import { resolvePageMetaDescription } from '@/lib/seo/description';
import { resolvePageTitle } from '@/lib/seo/title';
import { resolveOgImage } from '@/lib/seo/og-image';
import { buildPageName, type RouteAnalytics } from '@/analytics/data-layer';
import type { loader as rootLoader } from '@/root';
import { useTranslation } from 'react-i18next';

@PageType({
    name: 'Home Page',
    description: 'Main landing page with hero carousel, featured products, and help sections',
    supportedAspectTypes: [],
})
@RegionDefinition([
    {
        id: 'headerbanner',
        name: 'Header Banner Region',
        description: 'Region for promotional banners and hero content',
        maxComponents: 3,
    },
    {
        id: 'main',
        name: 'Main Content Region',
        description: 'Region for main content',
        maxComponents: 10,
    },
])
export class HomePageMetadata {}

export type HomePageData = {
    page: ReturnType<typeof fetchPageWithComponentData>;
    /** Footer background theme = the theme of the last component in `main`. Resolves with the page. */
    footerTheme?: Promise<DefaultSettingsTheme>;
    /** Site-wide JSON-LD (WebSite + Organization graph), emitted once here on the homepage (UCR-2783). */
    homepageSchema?: HomepageSchema;
    /** Adobe Analytics data-layer payload for this page (UCR-2938). */
    analytics?: RouteAnalytics;
    wishlistInitialState: Promise<WishlistInitialState>;
    pageUrl: string;
    /** Authored SEO Title from the page's SEO settings. Undefined when unset — the i18n title is used. (UCR-3060) */
    pageTitle?: string;
    /** Authored meta description from the page's SEO settings. Undefined when unset — no tag is emitted. */
    metaDescription?: string;
    ogImageUrl: string;
};

/**
 * How long the loader will wait on `getPage` purely to read SEO metadata off it. Past this the
 * homepage renders without a description/authored `og:image` rather than delaying the first byte
 * any further — see the note in {@link loader}.
 */
const PAGE_META_TIMEOUT_MS = 1500;

/**
 * Resolve `promise`, or `null` if it rejects or hasn't settled within `ms`. The promise itself keeps
 * running — only this view of it gives up.
 *
 * Both non-happy paths log. Failing soft here is deliberate (see {@link loader}), but failing soft
 * *silently* would mean the homepage could ship without a description or authored `og:image`
 * indefinitely — a Page Designer slowdown past the timeout looks identical to a page that simply has
 * no SEO content authored. The breadcrumb is the only way to tell those apart in production.
 */
function withTimeout<T>(promise: Promise<T>, ms: number, logger: Logger): Promise<T | null> {
    return new Promise((resolve) => {
        // A slow fetch that later rejects would otherwise reach both handlers and log the incident
        // twice — with the rejection message ("before SEO metadata could be read") reading as false
        // once the timeout has already given up. First outcome wins, and logs alone.
        let settled = false;
        const settle = (value: T | null) => {
            settled = true;
            resolve(value);
        };

        const timer = setTimeout(() => {
            logger.warn('HomePage: Page Designer fetch exceeded the SEO metadata budget', {
                timeoutMs: ms,
                impact: 'meta description omitted, og:image fell back to the site mark',
            });
            settle(null);
        }, ms);

        void promise.then(
            (value) => {
                clearTimeout(timer);
                if (settled) return;
                settle(value);
            },
            (error: unknown) => {
                clearTimeout(timer);
                if (settled) return;
                logger.warn('HomePage: Page Designer fetch rejected before SEO metadata could be read', {
                    error,
                    impact: 'meta description omitted, og:image fell back to the site mark',
                });
                settle(null);
            }
        );
    });
}

/**
 * Server-side loader function that fetches home page data.
 * This function runs on the server during SSR and prepares data for the home page.
 * @returns Promise that resolves to an object containing search result promise
 */
export async function loader(args: Route.LoaderArgs): Promise<HomePageData> {
    const logger = getLogger(args.context);
    logger.debug('HomePage: loader starting');

    const config = getConfig(args.context);
    const requestUrl = new URL(args.request.url);

    // Redirect bare "/" to the default site/locale prefixed homepage
    if (requestUrl.pathname === '/' && config.url?.prefix && config.url.prefix !== '/') {
        const siteRef = config.siteAliasMap?.[config.defaultSiteId] ?? config.defaultSiteId;
        const defaultSite = config.commerce.sites.find((s) => s.id === config.defaultSiteId);
        const defaultLocale = defaultSite?.defaultLocale ?? config.i18n.fallbackLng;
        const localeRef = config.localeAliasMap?.[defaultLocale] ?? defaultLocale;
        const prefixedPath = resolvePrefix({
            prefix: config.url.prefix,
            params: { siteId: siteRef, localeId: localeRef },
        });
        throw redirect(`${prefixedPath}/`);
    }

    // Resolved public origin (custom domain on MRT) rather than the lambda-internal `requestUrl.origin`,
    // so og:url matches the canonical link root.tsx emits from `getAppOrigin` (UCR-3023).
    const publicOrigin = getPublicOrigin(args.request);
    const pageUrl = buildCanonicalUrl(publicOrigin, requestUrl.pathname, requestUrl.search);

    // The page is still handed to `<Region>` as a promise, so per-component data keeps streaming and
    // the hero renders from the shell fallback (the headerbanner fallback below) while it arrives.
    const page = fetchPageWithComponentData(args, { pageId: 'homepage' });

    // ...but SEO metadata is authored *on* the page (UCR-3023) — the description comes from its SEO
    // Description, the og:image from the first image on it — so both need `getPage` resolved, and this
    // route previously never awaited it: doing so blocked the first byte, and a slow or unavailable
    // Page Designer backend then took the whole homepage down (SSR worker timeout → 502) instead of
    // just delaying below-hero content. Two guards keep that failure mode from returning — the await
    // is fail-soft AND time-boxed, so a sick backend costs the meta tags, never the page. Only
    // `getPage` is awaited here; component data promises are untouched.
    const pageForMeta = await withTimeout(page, PAGE_META_TIMEOUT_MS, logger);

    return {
        page,
        // Derived off the streamed page (no extra await, so the hero still renders first): the footer
        // continues the theme of the last component in `main`. Fail-soft to light if the page fetch
        // rejects (also avoids an unhandled rejection on this derived promise).
        footerTheme: page.then((p) => resolveLastComponentTheme(p, ['main'])).catch(() => 'light' as const),
        // Site-wide schema (UCR-2783): WebSite + Organization graph — built from config; absolute URLs
        // off the public origin.
        homepageSchema: generateHomepageSchema(publicOrigin, config),
        wishlistInitialState: fetchWishlistInitialState(args.context),
        pageUrl,
        // Authored SEO Title (UCR-3060). Deliberately no `page.name` fallback the way the Article and
        // Landing routes have one: the homepage's PD page is named "Homepage", which is a worse tab
        // title than the tuned i18n string the component falls back to.
        pageTitle: resolvePageTitle(pageForMeta?.pageTitle),
        metaDescription: resolvePageMetaDescription(pageForMeta),
        // The Home Page type declares no thumbnail attribute, so the share image is the first image on
        // the page — the hero in `headerbanner`, then `main` — falling back to the Black Magic mark.
        ogImageUrl: resolveOgImage({
            origin: publicOrigin,
            page: pageForMeta,
            regionIds: ['headerbanner', 'main'],
        }),
        analytics: { page: { pageName: buildPageName('home'), pageType: 'home' } },
    };
}

/**
 * Home page component. Content is authored entirely in Page Designer; the only fallback content is
 * the hero (HeroFallback — shown while the deferred page loads and when the region is empty/errors)
 * per UCR-2760.
 * @returns JSX element representing the home page layout
 */
export default function HomePage({ loaderData }: { loaderData: HomePageData }) {
    const { t } = useTranslation('home');
    const rootData = useRouteLoaderData<typeof rootLoader>('root');
    const nonce = rootData?.nonce ?? undefined;

    return (
        <WishlistProvider initialState={loaderData.wishlistInitialState}>
            {/* Site-wide JSON-LD (UCR-2783) — WebSite + Organization @graph, emitted once on the homepage. */}
            {loaderData.homepageSchema && (
                <JsonLd data={loaderData.homepageSchema} id="organization-schema" nonce={nonce} />
            )}
            {/* Dark wrapper background (the hero's own torn-edge reveal color, --bm-black). The page is
                deferred, so while authored content streams in the main region is briefly empty; without
                this the cream page background (--bm-organic-white) shows through and flashes white on
                desktop / leaves a bottom gap on mobile. min-h-screen makes the dark fill the viewport
                during that window. The homepage below the hero is dark-themed, so this is seamless once
                content arrives. (UCR-2763) */}
            <div className="-mt-8 min-h-screen" style={{ backgroundColor: 'var(--bm-black)' }}>
                {/* The page's authored SEO Title (Page Settings → SEO in Business Manager) wins, so a
                    content editor owns this without a deploy; the i18n string is the fallback for a
                    homepage with none set — or one whose page fetch timed out (UCR-3060).

                    `title` here also drives og:title/twitter:title (SeoMeta derives socialTitle from
                    it), and the homepage is the one page that passes openGraph — so this string is the
                    social-card headline as well as the tab title. Keep it descriptive, not "Home".
                    (UCR-2985) */}
                <SeoMeta
                    title={
                        loaderData.pageTitle ??
                        t('meta.title', { defaultValue: 'Hydroponics, Plant Nutrients & Grow Gear' })
                    }
                    description={loaderData.metaDescription}
                    openGraph={{
                        type: 'website',
                        url: loaderData.pageUrl,
                        image: loaderData.ogImageUrl,
                    }}
                />
                {/* Header Banner Region — the hero. Both the loading fallback (while the deferred page
                    resolves) and the empty/error fallback are the standalone hero (HeroFallback =
                    2_BlackMagic_Assets.hero), so the area renders as the hero throughout with no flash — and with
                    no dependency on the Layout heroCarousel skeleton (which used to flash a carousel
                    placeholder here and pull in embla). (UCR-2763) */}
                <div>
                    <Region
                        page={loaderData.page}
                        regionId="headerbanner"
                        fallbackOnEmpty
                        fallbackElement={<HeroFallback />}
                        errorElement={<HeroFallback />}
                    />
                </div>

                {/* Main Region — no fallback content; renders only authored Page Designer components. */}
                <Region page={loaderData.page} regionId="main" />
            </div>
        </WishlistProvider>
    );
}
